import React from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {
  Button,
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalContent,
  ModalHeader,
  NewCheckboxField,
  NewSelectField,
  NewTextInputField,
  SectionWithBorderField,
  showModal,
} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';
import {useOnChange} from '@edna/hooks';

import {StatusIcon} from 'src/components';
import {ModalCenterImage, Text} from 'src/components/primitives';
import {EChannelType} from 'src/constants';
import {PHONE_NUMBER_FORMAT} from 'src/containers/MessageMatchers/WhatsAppForm/definitions';
import {setPresetMessageMatcher} from 'src/containers/MessageMatchers/WhatsAppForm/slice';
import {useCheckForUniqueNameMutation} from 'src/containers/MessageMatchers/api';
import {
  EMessageMatcherButtonTypes,
  TMessageMatcher,
  TMessageMatcherButton,
} from 'src/containers/MessageMatchers/definitions';
import {hasVariable} from 'src/containers/MessageMatchers/utils';
import {useGetAllSubjectsQuery, useLazyGetSubjectsByWabaIdQuery} from 'src/containers/Subjects/api';
import {CongratsImage, MaintenancePurpleImage} from 'src/images';
import {EFixedCacheKey} from 'src/models';
import PATHS from 'src/paths';
import {prepareSubjectOptionsForSelect} from 'src/utils';

import {useGetMessageMatcherPresetQuery, useRegisterMessageMatcherPresetMutation} from './api';
import {TMessageMatcherPresetForm} from './definitions';
import * as S from './style';
import {prepareDataForEdit, resolver} from './utils';

type TProps = {
  closeModal: TEmptyFunction;
};

const SuccessModal = React.memo<TProps>(({closeModal}) => {
  const {t} = useTranslation();

  return (
    <Modal width="430px">
      <ModalHeader>{t('MessageMatcherPresets:successModal.title')}</ModalHeader>
      <ModalContent>
        <Text secondary>{t('MessageMatcherPresets:successModal.subtitle')}</Text>
        <ModalCenterImage as={CongratsImage} />
      </ModalContent>
      <ModalActions>
        <Button appearance="primary" onClick={closeModal}>
          {t('Common:button.understand')}
        </Button>
      </ModalActions>
    </Modal>
  );
});

SuccessModal.displayName = 'SuccessModal';

const showSuccessModal = () => showModal((closeModal) => <SuccessModal closeModal={closeModal} />);

const EditModal = React.memo<TProps & {messageMatcher: Partial<TMessageMatcher>}>(
  ({closeModal, messageMatcher}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleEditPreset = React.useCallback(() => {
      closeModal();
      dispatch(setPresetMessageMatcher(messageMatcher));
      navigate(
        `${PATHS.MESSAGE_MATCHERS_WHATSAPP_NEW}?${new URLSearchParams({duplicateFromPresetId: String(messageMatcher.id)})}`,
      );
    }, [navigate, messageMatcher]);

    return (
      <Modal width="430px">
        <ModalHeader>{t('MessageMatcherPresets:editModal.title')}</ModalHeader>
        <ModalContent>
          <Text secondary>{t('MessageMatcherPresets:editModal.subtitle')}</Text>
          <ModalCenterImage as={MaintenancePurpleImage} />
        </ModalContent>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
          <Button appearance="primary" onClick={handleEditPreset}>
            {t('Common:button.edit')}
          </Button>
        </ModalActions>
      </Modal>
    );
  },
);

EditModal.displayName = 'EditModal';

const showEditModal = (messageMatcher: Partial<TMessageMatcher>) =>
  showModal((closeModal) => <EditModal closeModal={closeModal} messageMatcher={messageMatcher} />);

const UrlButton = React.memo<{button: TMessageMatcherButton; index: number; hasCaption?: boolean}>(
  ({button, index, hasCaption}) => {
    const {t} = useTranslation();

    return (
      <Stack size="2" isFullWidth>
        {hasCaption && (
          <Text secondary weight={500} size="L">
            {t('MessageMatcherPresets:registrationModal.linkButton')}
          </Text>
        )}
        <SectionWithBorderField
          name={`buttons.${index}.url`}
          hint={t('MessageMatcherPresets:registrationModal.addUrl')}
          size="4"
        >
          <Text secondary size="S">
            <Trans
              i18nKey="MessageMatcherPresets:registrationModal.buttonText"
              values={{text: button.text}}
            >
              <Text isInline size="S" leftIndent="1" />
            </Trans>
          </Text>
          <Text secondary size="XS">
            <Trans
              i18nKey="MessageMatcherPresets:registrationModal.linkButtonHint"
              values={{text: button.text}}
            />
          </Text>
          <NewTextInputField
            label={
              <S.SmallLabel>{t('MessageMatcherPresets:registrationModal.staticUrl')}</S.SmallLabel>
            }
            name={`buttons.${index}.url`}
            noMargin
          />
          <NewCheckboxField name={`buttons.${index}.hasUrlPostfix`} noMargin>
            {t('MessageMatcherPresets:registrationModal.dynamicUrl')}
          </NewCheckboxField>
          <NewCheckboxField name={`buttons.${index}.shouldCollectClicks`} noMargin>
            {t('MessageMatcherPresets:registrationModal.collectClicks')}
          </NewCheckboxField>
        </SectionWithBorderField>
      </Stack>
    );
  },
);

UrlButton.displayName = 'UrlButton';

const PhoneButton = React.memo<{button: TMessageMatcherButton; index: number}>(
  ({button, index}) => {
    const {t} = useTranslation();

    return (
      <Stack size="2" isFullWidth>
        <Text secondary weight={500} size="L">
          {t('MessageMatcherPresets:registrationModal.phoneButton')}
        </Text>
        <SectionWithBorderField
          name={`buttons.${index}.phone`}
          hint={t('MessageMatcherPresets:registrationModal.addPhone')}
          size="4"
        >
          <Text secondary size="S">
            <Trans
              i18nKey="MessageMatcherPresets:registrationModal.buttonText"
              values={{text: button.text}}
            >
              <Text isInline size="S" leftIndent="1" />
            </Trans>
          </Text>
          <S.PhoneInputField
            label={
              <S.SmallLabel>{t('MessageMatcherPresets:registrationModal.phone')}</S.SmallLabel>
            }
            numberFormat={PHONE_NUMBER_FORMAT}
            name={`buttons.${index}.phone`}
            noMargin
          />
        </SectionWithBorderField>
      </Stack>
    );
  },
);

PhoneButton.displayName = 'PhoneButton';

const SubjectField = React.memo(() => {
  const {t} = useTranslation();

  const [getSubjectsByWabaId, {data: subjectsByWabaId = []}] = useLazyGetSubjectsByWabaIdQuery();
  const {data: subjects = []} = useGetAllSubjectsQuery({
    channelType: EChannelType.WHATSAPP,
    testing: false,
    locked: false,
  });

  const {watch, setValue} = useFormContext();
  const subjectId = watch('subjectId');

  const getSubjectsWithSameWabaId = React.useCallback(
    async (newSubjectId) => {
      if (!newSubjectId) {
        return;
      }

      try {
        const result = await getSubjectsByWabaId(newSubjectId).unwrap();

        const newSubjectsByWabaId = result?.length
          ? result
          : subjects.filter((option) => option.id === newSubjectId);

        setValue(
          'subjectIds',
          newSubjectsByWabaId.map((subject) => subject.id),
        );
      } catch (e) {}
    },
    [subjects],
  );

  React.useEffect(() => {
    getSubjectsWithSameWabaId(subjectId);
  }, [subjectId]);

  return (
    <SectionWithBorderField
      size="2"
      name="subjectId"
      label={t('MessageMatcherPresets:registrationModal.subject')}
    >
      <NewSelectField
        name="subjectId"
        noMargin
        options={prepareSubjectOptionsForSelect(subjects)}
        isValueCompareInSearch
      />
      {!!subjectsByWabaId.length && (
        <>
          <Stack size="2" direction="ROW">
            <Icon as={StatusIcon} name="info" size="16px" />
            <Text secondary size="XS">
              {t('MessageMatcherPresets:registrationModal.wabaHint')}
            </Text>
          </Stack>
          <S.WabaSubjects>
            {subjectsByWabaId.map((subject) => (
              <S.WabaSubject key={subject.id} isActive={subject.id === subjectId}>
                {subject.name}
              </S.WabaSubject>
            ))}
          </S.WabaSubjects>
        </>
      )}
    </SectionWithBorderField>
  );
});

SubjectField.displayName = 'SubjectField';

const NameField = React.memo<{presetName?: string}>(({presetName}) => {
  const {t} = useTranslation();

  const [
    checkForUniqueName,
    {data: resultCheckForUniqueName, reset: resetNameCheck, originalArgs: prevNameCheckArgs},
  ] = useCheckForUniqueNameMutation({fixedCacheKey: EFixedCacheKey.MESSAGE_MATCHER_UNIQUE_NAME});

  const {
    formState: {errors, submitCount},
    setError,
    clearErrors,
  } = useFormContext();

  React.useEffect(() => {
    if (presetName) {
      checkForUniqueName({channelType: EChannelType.WHATSAPP, name: presetName});
    }
  }, [presetName]);

  React.useEffect(() => {
    if (errors?.name) {
      return;
    }

    if (resultCheckForUniqueName?.existing) {
      return setError('name', {type: 'manual', message: t('Errors:message-matcher.name-exists')});
    }

    clearErrors('name');
  }, [resultCheckForUniqueName?.existing, submitCount, errors?.name]);

  const handleBlurName = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!errors?.name && prevNameCheckArgs?.name !== event.target.value) {
        checkForUniqueName({channelType: EChannelType.WHATSAPP, name: event.target.value});
      }
    },
    [errors?.name, prevNameCheckArgs],
  );

  return (
    <SectionWithBorderField
      name="name"
      label={t('MessageMatcherPresets:registrationModal.name')}
      hint={t('MessageMatcherPresets:registrationModal.nameHint')}
    >
      <NewTextInputField name="name" noMargin onChange={resetNameCheck} onBlur={handleBlurName} />
    </SectionWithBorderField>
  );
});

NameField.displayName = 'NameField';

const RegistrationModal = React.memo<TProps & {presetId: number}>(({closeModal, presetId}) => {
  const {t} = useTranslation();
  const [isShowExample, setIsShowExample] = React.useState(false);

  const {data: preset} = useGetMessageMatcherPresetQuery(presetId);
  const [registerPreset, {isLoading}] = useRegisterMessageMatcherPresetMutation();
  const [, {data: resultCheckForUniqueName}] = useCheckForUniqueNameMutation({
    fixedCacheKey: EFixedCacheKey.MESSAGE_MATCHER_UNIQUE_NAME,
  });
  const {data: subjects = []} = useGetAllSubjectsQuery({
    channelType: EChannelType.WHATSAPP,
    testing: false,
    locked: false,
  });

  const defaultValues = React.useMemo(
    () => ({
      name: preset?.name ?? '',
      buttons: preset?.content.buttons,
      subjectId: subjects.length === 1 ? subjects[0].id : null,
    }),
    [preset, subjects],
  );

  const methods = useForm<TMessageMatcherPresetForm>({
    mode: 'all',
    resolver,
    defaultValues,
    resetOptions: {keepErrors: true},
  });

  useOnChange(() => methods.reset(defaultValues), [defaultValues]);

  const handleRegister = React.useCallback(
    async (values) => {
      if (resultCheckForUniqueName?.existing || !preset?.id) {
        return;
      }

      try {
        await registerPreset({data: values, presetId: preset.id}).unwrap();
        closeModal();
        showSuccessModal();
      } catch (e) {}
    },
    [resultCheckForUniqueName?.existing, preset?.id],
  );

  const handleEdit = React.useCallback(() => {
    if (!preset) {
      return;
    }

    closeModal();
    showEditModal(prepareDataForEdit(methods.getValues(), preset));
  }, [preset, methods.getValues]);

  const handleChangeExampleView = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      setIsShowExample(event.target.checked);
    },
    [],
  );

  return (
    <Modal width="1006px">
      <FormProvider {...methods}>
        <ModalHeader>
          {t('MessageMatcherPresets:registrationModal.title', {name: preset?.name})}
        </ModalHeader>
        <ModalContent>
          <Stack size="5" direction="ROW">
            <Stack size="5" isFullWidth>
              <Text size="S">{t('MessageMatcherPresets:registrationModal.subTitle')}</Text>
              <NameField presetName={preset?.name} />
              <SubjectField />
              {preset?.content.buttons?.map((button, index, buttons) => (
                <React.Fragment key={index}>
                  {button.buttonType === EMessageMatcherButtonTypes.URL && (
                    <UrlButton
                      button={button}
                      index={index}
                      hasCaption={buttons[index - 1]?.buttonType !== EMessageMatcherButtonTypes.URL}
                    />
                  )}
                  {button.buttonType === EMessageMatcherButtonTypes.PHONE && (
                    <PhoneButton button={button} index={index} />
                  )}
                </React.Fragment>
              ))}
            </Stack>
            <S.MessagePreviewWrapper>
              {hasVariable(preset?.content?.text) && (
                <S.Switch
                  checked={isShowExample}
                  label={t('MessageMatcherPresets:registrationModal.showExample')}
                  onChange={handleChangeExampleView}
                />
              )}
              <S.MessagePreview
                channelType="WHATSAPP"
                contentType={preset?.contentType}
                language={preset?.language}
                content={isShowExample ? preset?.contentExample : preset?.content}
                type="OPERATOR"
                noActions
              />
            </S.MessagePreviewWrapper>
          </Stack>
        </ModalContent>
        <ModalActions>
          <ModalCancelButton onClick={handleEdit}>
            {t('MessageMatcherPresets:registrationModal.editButton')}
          </ModalCancelButton>
          <Button
            appearance="primary"
            onClick={methods.handleSubmit(handleRegister)}
            disabled={isLoading}
          >
            {t('Common:button.register')}
          </Button>
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

RegistrationModal.displayName = 'RegistrationModal';

export const showRegistrationModal = (presetId: number) =>
  showModal((closeModal) => <RegistrationModal presetId={presetId} closeModal={closeModal} />);
